import { useEffect, useRef, useState } from "react";
import { GraduationCap, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { logger } from "../lib/logger";
import { BreadcrumbJsonLd } from "../components/JsonLd";
import { CourseCard } from "../components/courses/CourseCard";
import { WeekPlanning } from "../components/courses/WeekPlanning";
import { EnrollForm } from "../components/courses/EnrollForm";
import { TeachersSection } from "../components/courses/TeachersSection";
import { NextSessionBanner, VenueSection } from "../components/courses/VenueSection";
import { FamilyGallery } from "../components/courses/FamilyGallery";
import { FamilyAgendaBox } from "../components/courses/FamilyAgendaBox";
import { PromoVideoWidget } from "../components/courses/PromoVideoWidget";

const withArticle = (name) => (/^[aeiouyéèê]/i.test(name || "") ? `l'${name}` : name);

const Stat = ({ n, label }) => (
  <div><div className="font-display text-5xl leading-none text-[color:var(--accent)]">{n}</div><div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-muted)] mt-1">{label}</div></div>
);

const share = async () => {
  const url = `${window.location.origin}/cours`;
  const text = "FEDEVO 2.0 — Saison 2026-2027 à Vaulx-en-Velin : cours de hip-hop, breakdance et street art. Pré-inscris-toi !";
  try {
    if (navigator.share) { await navigator.share({ title: "FEDEVO 2.0 · Les cours", text, url }); return; }
    await navigator.clipboard.writeText(url);
    toast.success("Lien copié");
  } catch (err) { if (err?.name !== "AbortError") toast.error("Impossible de partager"); }
};

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [season, setSeason] = useState(null);
  const [preselect, setPreselect] = useState("");
  const [trialPrefill, setTrialPrefill] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    api.get("/courses").then(r => setCourses(r.data)).catch(err => logger.warn("[courses] list failed:", err));
    api.get("/courses/season").then(r => setSeason(r.data)).catch(err => logger.warn("[courses] season failed:", err));
  }, []);

  const goEnroll = (course) => {
    setPreselect(course ? course.id : "");
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const bookTrial = (session, date) => {
    setTrialPrefill({ course_id: session.course_id, date });
    toast.success(`Cours d'essai gratuit pré-rempli : ${session.course_name} le ${new Date(`${date}T12:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}`);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const slotsPerCourse = courses[0]?.slots?.length || 2;
  const hoursPerWeek = courses.reduce((sum, c) => sum + (c.slots || []).length, 0);

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10 space-y-14" data-testid="courses-page">
      <BreadcrumbJsonLd items={[{ name: "Accueil", path: "/" }, { name: "Les cours", path: "/cours" }]} />

      <section className="grid lg:grid-cols-[1.3fr_1fr] gap-8 items-end">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--accent)] mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4" /> {season?.label || "Saison 2026 — 2027"} · {season?.city?.replace(/^\d+\s*/, "") || "Vaulx-en-Velin"}</div>
          <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.9]" data-testid="courses-title">Hip-hop.<br />Break.<br />Street art.</h1>
          <p className="text-[color:var(--text-muted)] mt-5 max-w-xl">La nouvelle saison de cours urbains pour enfants et ados à {withArticle(season?.venue_name) || "l'Espace Carco"} : trois disciplines, des profs au top et des places limitées. Pré-inscris-toi dès maintenant.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button type="button" onClick={() => goEnroll(null)} data-testid="courses-cta-enroll" className="inline-flex items-center gap-2 bg-[color:var(--accent)] text-black font-semibold px-6 py-3 rounded-full text-xs uppercase tracking-widest hover:brightness-110 transition">Je réserve ma place</button>
            <a href="#cours" className="inline-flex items-center gap-2 border border-[color:var(--border)] hover:border-white px-6 py-3 rounded-full text-xs uppercase tracking-widest transition-colors">Découvrir les cours</a>
            <button type="button" onClick={share} data-testid="courses-share" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--text-muted)] hover:text-[color:var(--accent)] px-2 transition-colors"><Share2 className="w-4 h-4" /> Partager</button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 glass rounded-2xl p-6 self-start">
          <Stat n={courses.length || 3} label="disciplines" /><Stat n={slotsPerCourse} label="niveaux par cours" /><Stat n={`${hoursPerWeek || 6}h`} label="par semaine" />
          <div className="col-span-3"><PromoVideoWidget compact className="max-w-[260px] mx-auto" /></div>
        </div>
      </section>

      <NextSessionBanner season={season} />

      <section id="cours" className="space-y-6 scroll-mt-24">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--accent)] mb-2">Les cours</div>
          <h2 className="font-display text-4xl sm:text-5xl leading-none">Trois disciplines, une seule ambiance.</h2>
          <p className="text-sm text-[color:var(--text-muted)] mt-2 max-w-2xl">Chaque atelier est ouvert aux débutants comme aux intermédiaires. Choisis ton jour, réserve ta place et viens essayer.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger" data-testid="courses-grid">
          {courses.map(c => <CourseCard key={c.id} course={c} onEnroll={goEnroll} season={season} />)}
        </div>
      </section>

      <TeachersSection />

      <section className="space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--accent)] mb-2">Planning de la saison</div>
          <h2 className="font-display text-4xl sm:text-5xl leading-none">Semaine par semaine, on ne rate rien.</h2>
        </div>
        <WeekPlanning season={season} onTrial={bookTrial} />
        <FamilyAgendaBox />
      </section>

      <VenueSection season={season} />

      <FamilyGallery courses={courses} />

      <section ref={formRef} id="inscription" className="space-y-6 scroll-mt-24">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--accent)] mb-2">Pré-inscription</div>
          <h2 className="font-display text-4xl sm:text-5xl leading-none">Bloque ta place pour {season?.label?.replace("Saison ", "") || "2026-2027"}.</h2>
          <p className="text-sm text-[color:var(--text-muted)] mt-2 max-w-2xl">Remplis le formulaire : on te recontacte pour valider l'inscription et réserver ta place. Les groupes sont limités ! 1er cours d'essai offert, puis règlement en ligne (mois, trimestre ou année) juste après.</p>
        </div>
        <EnrollForm courses={courses} season={season} preselect={preselect} trialPrefill={trialPrefill} />
      </section>
    </div>
  );
}
